import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { 
  ArrowLeft,
  ArrowRight,
  Package,
  User,
  Users,
  CheckCircle,
  Plus,
  Trash2,
  Gift
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatCurrency } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth.store';

interface BenefitPlan {
  id: string;
  name: string;
  description: string;
  category: string;
  monthlyPrice: string;
  dependentPrice: string;
  enrollmentFee: string;
  maxDependents: number;
  waitingPeriodDays: number;
  requiresApproval: boolean;
  subsidyType?: string;
  subsidyValue?: string;
}

interface Dependent {
  name: string;
  cpf: string;
  birthDate: string;
  relationship: string;
}

const WIZARD_STEPS = [
  { id: 'plan', title: 'Plano', icon: Package },
  { id: 'personal', title: 'Dados Pessoais', icon: User },
  { id: 'dependents', title: 'Dependentes', icon: Users },
  { id: 'review', title: 'Confirmação', icon: CheckCircle },
];

const relationshipOptions = [
  { value: 'SPOUSE', label: 'Cônjuge' },
  { value: 'CHILD', label: 'Filho(a)' },
  { value: 'PARENT', label: 'Pai/Mãe' },
  { value: 'SIBLING', label: 'Irmão(ã)' },
  { value: 'OTHER', label: 'Outro' },
];

export function BenefitSubscribe() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [dependents, setDependents] = useState<Dependent[]>([]);
  const [newDependent, setNewDependent] = useState<Dependent>({
    name: '',
    cpf: '',
    birthDate: '',
    relationship: 'SPOUSE',
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const { data: plan, isLoading } = useQuery<BenefitPlan>({
    queryKey: ['benefit-plan', planId],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.benefits.planDetails(planId!));
      return response.data;
    },
    enabled: !!planId,
  });

  const subscribeMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(ENDPOINTS.benefits.subscribe, {
        planId,
        dependents: dependents.length > 0 ? dependents : undefined,
      });
      return response.data;
    },
    onSuccess: () => {
      navigate('/banking/my-subscriptions');
    },
  });

  const handleAddDependent = () => {
    if (newDependent.name && newDependent.cpf && newDependent.birthDate) {
      setDependents([...dependents, newDependent]);
      setNewDependent({
        name: '',
        cpf: '',
        birthDate: '',
        relationship: 'SPOUSE',
      });
    }
  };

  const handleRemoveDependent = (index: number) => {
    setDependents(dependents.filter((_, i) => i !== index));
  };

  const handleNext = () => {
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    if (acceptedTerms) {
      subscribeMutation.mutate();
    }
  };

  const calculateTotal = () => {
    if (!plan) return { monthly: 0, enrollment: 0, subsidy: 0, net: 0 };
    
    const monthlyPrice = Number(plan.monthlyPrice) / 100;
    const dependentPrice = Number(plan.dependentPrice) / 100;
    const enrollmentFee = Number(plan.enrollmentFee) / 100;
    
    const monthly = monthlyPrice + (dependents.length * dependentPrice);
    
    let subsidy = 0;
    if (plan.subsidyType === 'PERCENTAGE' && plan.subsidyValue) {
      subsidy = monthly * (Number(plan.subsidyValue) / 10000);
    } else if (plan.subsidyType === 'FIXED' && plan.subsidyValue) {
      subsidy = Number(plan.subsidyValue) / 100;
    }
    
    return {
      monthly,
      enrollment: enrollmentFee,
      subsidy,
      net: monthly - subsidy,
    };
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-surface-700 rounded w-48 animate-pulse" />
        <Card className="animate-pulse">
          <div className="h-64 bg-surface-700 rounded" />
        </Card>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/banking/benefits')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <Card className="text-center py-12">
          <Gift className="w-12 h-12 text-surface-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">
            Plano não encontrado
          </h3>
        </Card>
      </div>
    );
  }

  const totals = calculateTotal();

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <Button variant="ghost" onClick={() => navigate(`/banking/benefits/${planId}`)}>
        <ArrowLeft className="w-4 h-4 mr-2" />
        Voltar aos Detalhes
      </Button>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Adesão ao Benefício</h1>
        <p className="text-surface-400 mt-1">{plan.name}</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center justify-between mb-8">
        {WIZARD_STEPS.map((step, index) => {
          const Icon = step.icon;
          const isActive = index === currentStep;
          const isCompleted = index < currentStep;
          
          return (
            <div key={step.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`
                    w-10 h-10 rounded-full flex items-center justify-center transition-colors
                    ${isActive ? 'bg-primary-500 text-white' :
                      isCompleted ? 'bg-green-500 text-white' :
                      'bg-surface-700 text-surface-400'}
                  `}
                >
                  {isCompleted ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={`text-xs mt-2 ${isActive ? 'text-primary-400' : 'text-surface-400'}`}>
                  {step.title}
                </span>
              </div>
              {index < WIZARD_STEPS.length - 1 && (
                <div
                  className={`w-16 h-0.5 mx-2 ${
                    isCompleted ? 'bg-green-500' : 'bg-surface-700'
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Step Content */}
      <Card>
        {/* Step 0: Plan Confirmation */}
        {currentStep === 0 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-white">Confirme o Plano</h2>
            <div className="bg-surface-800 rounded-lg p-4">
              <h3 className="text-white font-medium">{plan.name}</h3>
              <p className="text-surface-400 text-sm mt-1">{plan.description}</p>
              <div className="flex items-baseline gap-2 mt-4">
                <span className="text-2xl font-bold text-primary-400">
                  {formatCurrency(Number(plan.monthlyPrice) / 100)}
                </span>
                <span className="text-surface-400">/mês</span>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Personal Data */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-white">Seus Dados</h2>
            <p className="text-surface-400 text-sm">
              Confirme seus dados pessoais para a adesão
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Nome Completo"
                value={user?.name || ''}
                disabled
              />
              <Input
                label="E-mail"
                value={user?.email || ''}
                disabled
              />
              <Input
                label="CPF/CNPJ"
                value={user?.cpfCnpj || ''}
                disabled
              />
              <Input
                label="Telefone"
                value={user?.phone || ''}
                disabled
              />
            </div>
          </div>
        )}

        {/* Step 2: Dependents */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white">Dependentes</h2>
                <p className="text-surface-400 text-sm">
                  {plan.maxDependents > 0
                    ? `Você pode adicionar até ${plan.maxDependents} dependentes`
                    : 'Este plano não permite dependentes'}
                </p>
              </div>
              {plan.maxDependents > 0 && (
                <Badge variant="info">
                  {dependents.length}/{plan.maxDependents}
                </Badge>
              )}
            </div>

            {/* Dependents List */}
            {dependents.length > 0 && (
              <div className="space-y-2">
                {dependents.map((dep, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between bg-surface-800 rounded-lg p-3"
                  >
                    <div>
                      <p className="text-white">{dep.name}</p>
                      <p className="text-sm text-surface-400">
                        {relationshipOptions.find(r => r.value === dep.relationship)?.label}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveDependent(index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Add Dependent Form */}
            {plan.maxDependents > 0 && dependents.length < plan.maxDependents && (
              <div className="border border-surface-700 rounded-lg p-4 space-y-4">
                <h3 className="text-sm font-medium text-surface-300">Adicionar Dependente</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Nome Completo"
                    value={newDependent.name}
                    onChange={(e) => setNewDependent({ ...newDependent, name: e.target.value })}
                    placeholder="Nome do dependente"
                  />
                  <Input
                    label="CPF"
                    value={newDependent.cpf}
                    onChange={(e) => setNewDependent({ ...newDependent, cpf: e.target.value })}
                    placeholder="000.000.000-00"
                  />
                  <Input
                    label="Data de Nascimento"
                    type="date"
                    value={newDependent.birthDate}
                    onChange={(e) => setNewDependent({ ...newDependent, birthDate: e.target.value })}
                  />
                  <Select
                    label="Parentesco"
                    value={newDependent.relationship}
                    onChange={(e) => setNewDependent({ ...newDependent, relationship: e.target.value })}
                    options={relationshipOptions}
                  />
                </div>
                <Button
                  variant="secondary"
                  onClick={handleAddDependent}
                  disabled={!newDependent.name || !newDependent.cpf || !newDependent.birthDate}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Dependente
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Review */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-white">Revisão e Confirmação</h2>
            
            {/* Summary */}
            <div className="bg-surface-800 rounded-lg p-4 space-y-4">
              <div className="flex justify-between">
                <span className="text-surface-400">Plano</span>
                <span className="text-white">{plan.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-400">Titular</span>
                <span className="text-white">{user?.name}</span>
              </div>
              {dependents.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Dependentes</span>
                  <span className="text-white">{dependents.length}</span>
                </div>
              )}
            </div>

            {/* Pricing */}
            <div className="bg-surface-800 rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-surface-400">Mensalidade</span>
                <span className="text-white">{formatCurrency(totals.monthly)}</span>
              </div>
              {totals.subsidy > 0 && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Subsídio</span>
                  <span className="text-green-400">-{formatCurrency(totals.subsidy)}</span>
                </div>
              )}
              {totals.enrollment > 0 && (
                <div className="flex justify-between">
                  <span className="text-surface-400">Taxa de adesão (única)</span>
                  <span className="text-white">{formatCurrency(totals.enrollment)}</span>
                </div>
              )}
              <div className="border-t border-surface-700 pt-3 flex justify-between">
                <span className="text-white font-medium">Valor Mensal</span>
                <span className="text-primary-400 text-xl font-bold">
                  {formatCurrency(totals.net)}
                </span>
              </div>
            </div>

            {/* Terms */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="w-5 h-5 mt-0.5 rounded border-surface-600 bg-surface-700 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-surface-300 text-sm">
                Li e aceito os termos de uso e condições do plano. Autorizo o desconto em folha
                de pagamento conforme valores apresentados.
              </span>
            </label>

            {plan.requiresApproval && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-yellow-400 text-sm">
                  Sua adesão será analisada pelo administrador antes de ser ativada.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-8 pt-6 border-t border-surface-700">
          <Button
            variant="secondary"
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          
          {currentStep < WIZARD_STEPS.length - 1 ? (
            <Button onClick={handleNext}>
              Próximo
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!acceptedTerms || subscribeMutation.isPending}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              {subscribeMutation.isPending ? 'Processando...' : 'Confirmar Adesão'}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}

export default BenefitSubscribe;
